import { createApp, markRaw } from "vue";
import "./style.css";
import App from "./App.vue";
import { createPinia } from "pinia";
import router from "@/router";
import Toast, { PluginOptions } from "vue-toastification";
import "vue-toastification/dist/index.css";
import VueCameraLib from 'vue-camera-lib'
import * as Sentry from "@sentry/vue";
import { registerSW } from 'virtual:pwa-register'
import { shouldReportError } from "@/helpers/sentryErrors";
import { initializeWebAnalytics } from "@/helpers/analytics";
import { auth } from "@/firebase";

const updateSW = registerSW({
  onNeedRefresh() {
    // Silent reload
    updateSW(true)
  },
  onOfflineReady() {
    console.log('App ready to work offline')
  }
})

const pinia = createPinia();

const toastOptions: PluginOptions = {
  timeout: 2500,
};


const app = createApp(App);

const enableProdSentry = !import.meta.env.DEV && Boolean(import.meta.env.VITE_SENTRY_DSN);
const configuredTraceSampleRate = Number(
  import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "0.1"
);
const tracesSampleRate =
  Number.isFinite(configuredTraceSampleRate) &&
  configuredTraceSampleRate >= 0 &&
  configuredTraceSampleRate <= 1
    ? configuredTraceSampleRate
    : 0.1;
// Only enable Spotlight in dev mode and ensure the function exists safely
const enableSpotlight = import.meta.env.DEV && 
  typeof Sentry === "object" && 
  Sentry !== null && 
  typeof (Sentry as any).spotlightBrowserIntegration === "function";

if (enableProdSentry || enableSpotlight) {
  type SentryInitOptions = NonNullable<Parameters<typeof Sentry.init>[0]>;

  const integrations = [
    ...(enableProdSentry ? [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ] : []),
    ...(enableSpotlight ? [(Sentry as any).spotlightBrowserIntegration()] : []),
  ] as SentryInitOptions["integrations"];

  // Spotlight gives richer debugging locally; production tracing stays isolated
  const sentryOptions: SentryInitOptions = {
    dsn: enableProdSentry ? import.meta.env.VITE_SENTRY_DSN : undefined,
    integrations,
    tracesSampleRate: enableProdSentry ? tracesSampleRate : 0,
    tracePropagationTargets: [
      window.location.origin,
      /^https:\/\/[a-z0-9-]+-[a-z0-9-]+\.cloudfunctions\.net/i,
      /^https:\/\/[a-z0-9-]+-[a-z0-9-]+\.run\.app/i,
    ],
    replaysSessionSampleRate: enableProdSentry ? 0.1 : 0,
    replaysOnErrorSampleRate: enableProdSentry ? 1.0 : 0,
    environment: import.meta.env.MODE,
    enabled: enableProdSentry || enableSpotlight,
    beforeSend(event, hint) {
      if (
        hint?.originalException &&
        !shouldReportError(hint.originalException)
      ) {
        return null;
      }

      return event;
    },
  };

  Sentry.init(sentryOptions);
}

initializeWebAnalytics(router, auth);

app.use(VueCameraLib);
app.use(Toast, toastOptions);
app.use(pinia);
app.use(router);
app.mount("#app");
